import type { BomItem, ForecastState } from "../types";

/** Bump when forecast cache shape changes (invalidates stale desktop parse cache). */
export const FORECAST_CACHE_SCHEMA_VERSION = 3;

/** Item confidence below this requires explicit HITL review before approve. */
export const LOW_CONFIDENCE_REVIEW_THRESHOLD = 0.65;

export type ForecastCacheIssue =
  | "missing_drawing_features"
  | "missing_production_intel"
  | "schema_version_stale";

export interface ForecastCacheStatus {
  stale: boolean;
  schema_version: number;
  expected_schema_version: number;
  issues: ForecastCacheIssue[];
}

/** Available stock qty from engine matched_stock payload. */
export function stockQtyFromItem(item: BomItem): number | null {
  const qty = item.matched_stock?.stock_qty;
  return qty != null && Number.isFinite(qty) ? qty : null;
}

export function riskBadgeClass(level?: string): string {
  switch ((level || "").toLocaleUpperCase("en-US")) {
    case "GREEN":
      return "pill green";
    case "YELLOW":
      return "pill yellow";
    case "RED":
      return "pill red";
    default:
      return "pill";
  }
}

export interface TitleBlockFields {
  drawing_number?: string | null;
  revision?: string | null;
  title?: string | null;
  customer?: string | null;
  date?: string | null;
}

function mergeDxfFeatures(
  primary: Record<string, unknown> | undefined,
  nested: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!primary && !nested) return undefined;
  if (!nested) return primary;
  if (!primary) return nested;
  return {
    ...nested,
    ...primary,
    drawing_dimensions: primary.drawing_dimensions ?? nested.drawing_dimensions,
    drawing_features: primary.drawing_features ?? nested.drawing_features,
    title_block: primary.title_block ?? nested.title_block,
    preview_2d: primary.preview_2d ?? nested.preview_2d,
    geo: { ...(nested.geo as object), ...(primary.geo as object) },
    derived: { ...(nested.derived as object), ...(primary.derived as object) },
  };
}

/** Read engine title_block from enriched forecast dxf_features. */
export function titleBlockFromForecast(f: ForecastState): TitleBlockFields | null {
  const merged = mergeDxfFeatures(
    f.dxf_features as Record<string, unknown> | undefined,
    (f.production_intel?.dxf_features ?? f.production_intel) as Record<string, unknown> | undefined,
  );
  const tb = merged?.title_block;
  if (!tb || typeof tb !== "object") return null;
  return tb as TitleBlockFields;
}

function readCacheSchemaVersion(f: ForecastState): number {
  const raw = (f as ForecastState & { cache_schema_version?: number }).cache_schema_version;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 1;
}

/**
 * STEP/STP assemblies use OCC mesh + derived geometry — they never produce DXF
 * `drawing_features` (DIMENSION entities). Requiring that field would false-flag
 * every healthy STEP parse as "stale cache".
 *
 * drawing_id/document_name often omit the extension (engine uses part title), so
 * also trust detection_method and 3D derived fields.
 */
export function isStepLikeForecast(
  f: ForecastState,
  merged?: Record<string, unknown> | null,
): boolean {
  const name = (f.document_name || f.drawing_id || "").toLowerCase();
  if (/\.ste?p$/i.test(name)) return true;

  const method = (f.detection_method || "").toLowerCase();
  if (
    method.includes("step") ||
    method.includes("stp") ||
    method.includes("occ") ||
    method.includes("neural_step")
  ) {
    return true;
  }

  const features = merged ?? (f.dxf_features as Record<string, unknown> | undefined);
  const derived = (features?.derived ?? {}) as Record<string, unknown>;
  if (derived.mesh && typeof derived.mesh === "object") return true;
  if (derived.is_real_3d === true) return true;
  if (derived.occ_ok === true || derived.volume_m3 != null) return true;
  if (Array.isArray(derived.assembly_components) && derived.assembly_components.length > 0) {
    return true;
  }
  // Engine STEP path tags source on dxf_features
  const source = String(features?.source ?? "").toLowerCase();
  if (source.includes("step") || source === "stp") return true;
  return false;
}

/** Detect stale parse cache missing drawing_features / production_intel (Faz 1). */
export function assessForecastCache(f: ForecastState): ForecastCacheStatus {
  const schema_version = readCacheSchemaVersion(f);
  const issues: ForecastCacheIssue[] = [];

  if (schema_version < FORECAST_CACHE_SCHEMA_VERSION) {
    issues.push("schema_version_stale");
  }

  const merged = mergeDxfFeatures(
    f.dxf_features as Record<string, unknown> | undefined,
    (f.production_intel?.dxf_features ?? f.production_intel) as Record<string, unknown> | undefined,
  );
  const stepLike = isStepLikeForecast(f, merged);

  // DXF path expects drawing_features; STEP path expects 3D derived/mesh instead.
  if (!stepLike && !merged?.drawing_features) {
    issues.push("missing_drawing_features");
  }
  if (!f.production_intel) {
    issues.push("missing_production_intel");
  }

  const contentIssues = issues.filter((i) => i !== "schema_version_stale");
  return {
    stale: contentIssues.length > 0,
    schema_version,
    expected_schema_version: FORECAST_CACHE_SCHEMA_VERSION,
    issues,
  };
}

/** Merge dxf_features from production_intel and stamp cache schema version. */
export function enrichForecast(f: ForecastState): ForecastState {
  const nested = (f.production_intel?.dxf_features ?? f.production_intel) as
    | Record<string, unknown>
    | undefined;
  const merged = mergeDxfFeatures(f.dxf_features, nested);
  const withFeatures =
    !merged || merged === f.dxf_features ? f : { ...f, dxf_features: merged };
  const cache = assessForecastCache(withFeatures);
  return {
    ...withFeatures,
    cache_schema_version: FORECAST_CACHE_SCHEMA_VERSION,
    cache_stale: cache.stale,
    cache_issues: cache.issues,
  } as ForecastState;
}

/** BOM rows that must be reviewed before approve (low per-item confidence). */
export function lowConfidenceItems(items: BomItem[] | undefined): BomItem[] {
  if (!items?.length) return [];
  return items.filter((it) => it.confidence < LOW_CONFIDENCE_REVIEW_THRESHOLD);
}

/** True when every low-confidence row has a matching reviewed_items override. */
export function lowConfidenceReviewComplete(
  items: BomItem[] | undefined,
  reviewed: BomItem[] | undefined,
): boolean {
  const pending = lowConfidenceItems(items);
  if (pending.length === 0) return true;
  const reviewedNos = new Set((reviewed ?? []).map((r) => r.item_no));
  return pending.every((it) => reviewedNos.has(it.item_no));
}